// pages/admin/LiveTracking.jsx

import { useEffect, useState } from 'react';
import {
    MapPin,
    RefreshCw,
    User,
    Clock,
    Navigation,
    Eye,
    ExternalLink,
    Activity,
    Calendar
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Loading } from '../../components/common/Loading';
import Navbar from '../../components/header/Navbar';
import { useAuth } from '../../hooks/useAuth';
import {
    getCheckedInUsers,
    getLocationHistory,
    getLocationName,
    getTodayString
} from '../../services/attendanceService';

const LiveTracking = () => {
    const { user, loading: isLoading } = useAuth();
    const [checkedInUsers, setCheckedInUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedUser, setSelectedUser] = useState(null);
    const [locationHistory, setLocationHistory] = useState([]);
    const [selectedUserData, setSelectedUserData] = useState(null);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [lastRefresh, setLastRefresh] = useState(new Date());

    useEffect(() => {
        loadData();

        let interval = null;
        if (autoRefresh) {
            interval = setInterval(loadData, 30000); // Refresh every 30 seconds
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [autoRefresh]);

    const loadData = async () => {
        try {
            const users = await getCheckedInUsers();
            setCheckedInUsers(users);
            setLastRefresh(new Date());

            // Update selected user data if still checked in
            if (selectedUser) {
                const updatedUser = users.find(u => u.userId === selectedUser);
                if (updatedUser) {
                    setSelectedUserData(updatedUser);
                    // Refresh location history
                    await loadUserHistory(selectedUser);
                } else {
                    // User is no longer checked in
                    setSelectedUser(null);
                    setSelectedUserData(null);
                    setLocationHistory([]);
                }
            }
        } catch (error) {
            console.error('Error loading checked-in users:', error);
            toast.error('Failed to load user data');
        } finally {
            setLoading(false);
        }
    };

    // const loadUserHistory = async (userId) => {
    //     try {
    //         const history = await getLocationHistory(userId);
    //         setLocationHistory(history);
    //     } catch (error) {
    //         console.error('Error loading location history:', error);
    //         toast.error('Failed to load location history');
    //     }
    // };

    const loadUserHistory = async (userId) => {
        try {
            const history = await getLocationHistory(userId);

            // Resolve location names for each history item using Promise.all
            const historyWithNames = await Promise.all(
                history.map(async (loc) => {
                    const lat = loc.location?.latitude ?? loc.latitude;
                    const lng = loc.location?.longitude ?? loc.longitude;
                    const locationName = await getLocationName(lat, lng);

                    return {
                        ...loc,
                        locationName
                    };
                })
            );

            setLocationHistory(historyWithNames);
        } catch (error) {
            console.error('Error loading location history:', error);
            toast.error('Failed to load location history');
        }
    };



    const handleSelectUser = async (userId) => {
        if (selectedUser === userId) {
            setSelectedUser(null);
            setSelectedUserData(null);
            setLocationHistory([]);
            return;
        }

        const userData = checkedInUsers.find(u => u.userId === userId);
        setSelectedUser(userId);
        setSelectedUserData(userData || null);
        await loadUserHistory(userId);
    };

    const formatDuration = (minutes) => {
        if (!minutes) return '0h 0m';
        const hours = Math.floor(minutes / 60);
        const mins = Math.floor(minutes % 60);
        return `${hours}h ${mins}m`;
    };

    const getStatusBadge = (status) => {
        const configs = {
            'checked-in': { color: 'bg-green-100 text-green-700', label: 'Active' },
            'on-break': { color: 'bg-yellow-100 text-yellow-700', label: 'On Break' }
        };
        return configs[status] || configs['checked-in'];
    };

    // Get user's latest location
    const getLatestLocation = () => {
        if (locationHistory.length === 0) return null;
        return locationHistory[locationHistory.length - 1];
    };

    const latestLocation = getLatestLocation();

    if (loading || isLoading) return <Loading />;

    return (
        <>
            <Navbar user={user} />
            <div className="max-w-7xl mx-auto px-4 py-6">
                {/* Header */}
                <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                            <Navigation className="w-6 h-6 text-blue-600" />
                            Live Tracking
                        </h1>
                        <p className="text-sm text-gray-500">
                            Real-time location of field employees •
                            <span className="text-green-600 font-medium ml-1">
                                {checkedInUsers.length} active
                            </span>
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 text-xs text-gray-400">
                            <span>Auto-refresh</span>
                            <button
                                onClick={() => setAutoRefresh(!autoRefresh)}
                                className={`px-3 py-1 rounded-lg text-xs font-medium transition ${autoRefresh
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-gray-100 text-gray-500'
                                    }`}
                            >
                                {autoRefresh ? 'On' : 'Off'}
                            </button>
                        </div>
                        <button
                            onClick={loadData}
                            className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition flex items-center gap-1 text-sm"
                        >
                            <RefreshCw className="w-4 h-4" />
                            Refresh
                        </button>
                        <span className="text-xs text-gray-400">
                            Last updated: {lastRefresh.toLocaleTimeString()}
                        </span>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Employee List */}
                    <div className="lg:col-span-1 bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                        <div className="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                                <User className="w-4 h-4" />
                                Active Employees
                                <span className="ml-auto bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">
                                    {checkedInUsers.length}
                                </span>
                            </h3>
                        </div>

                        <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
                            {checkedInUsers.length === 0 ? (
                                <div className="p-8 text-center text-gray-500">
                                    <div className="text-4xl mb-2">📍</div>
                                    <p className="font-medium">No active employees</p>
                                    <p className="text-sm text-gray-400">No one is currently checked in</p>
                                </div>
                            ) : (
                                checkedInUsers.map((record) => {
                                    const status = getStatusBadge(record.status);
                                    const sessionCount = record.sessions?.length || 0;
                                    const isSelected = selectedUser === record.userId;

                                    return (
                                        <button
                                            key={record.id}
                                            onClick={() => handleSelectUser(record.userId)}
                                            className={`w-full flex items-center gap-3 p-4 transition ${isSelected
                                                ? 'bg-blue-50'
                                                : 'hover:bg-gray-50'
                                                }`}
                                        >
                                            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-400 to-purple-400 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                                                {record.userName?.charAt(0) || 'U'}
                                            </div>
                                            <div className="flex-1 text-left min-w-0">
                                                <p className="font-medium text-gray-800 truncate">
                                                    {record.userName || 'Unknown'}
                                                </p>
                                                <div className="flex items-center gap-2 mt-0.5">
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${status.color}`}>
                                                        {status.label}
                                                    </span>
                                                    <span className="text-[10px] text-gray-400">
                                                        {sessionCount} session{sessionCount > 1 ? 's' : ''}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="flex-shrink-0">
                                                <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse"></div>
                                            </div>
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </div>

                    {/* Location Details */}
                    <div className="lg:col-span-2 bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                        {!selectedUser ? (
                            <div className="p-12 text-center text-gray-500">
                                <div className="text-6xl mb-4">📍</div>
                                <p className="font-medium text-lg">Select an employee</p>
                                <p className="text-sm text-gray-400">Choose an active employee to view their location</p>
                            </div>
                        ) : (
                            <>
                                {/* User Header */}
                                <div className="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white flex flex-wrap justify-between items-center gap-2">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-400 to-purple-400 flex items-center justify-center text-white font-semibold">
                                            {selectedUserData?.userName?.charAt(0) || 'U'}
                                        </div>
                                        <div>
                                            <p className="font-semibold text-gray-800">
                                                {selectedUserData?.userName || 'Unknown'}
                                            </p>
                                            <p className="text-xs text-gray-400">
                                                {selectedUserData?.sessions?.length || 0} sessions •
                                                Total: {formatDuration(selectedUserData?.totalWorkDuration || 0)}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${selectedUserData?.status === 'on-break'
                                            ? 'bg-yellow-100 text-yellow-700'
                                            : 'bg-green-100 text-green-700'
                                            }`}>
                                            {selectedUserData?.status === 'on-break' ? 'On Break' : 'Active'}
                                        </span>
                                    </div>
                                </div>

                                {/* Location Details */}
                                <div className="p-4 space-y-4">
                                    {/* Current Location */}
                                    <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                                        <h4 className="text-sm font-medium text-blue-800 mb-2 flex items-center gap-2">
                                            <Navigation className="w-4 h-4" />
                                            Latest Location
                                        </h4>
                                        {latestLocation ? (
                                            <div className="space-y-1">
                                                <p className="text-sm text-blue-700 font-mono">
                                                    {latestLocation.location?.latitude?.toFixed(6)}, {latestLocation.location?.longitude?.toFixed(6)}
                                                </p>
                                                <div className="flex items-center gap-4 text-xs text-blue-600">
                                                    <span>
                                                        Accuracy: ±{latestLocation.location?.accuracy?.toFixed(0) || '?'}m
                                                    </span>
                                                    <span>
                                                        Updated: {new Date(latestLocation.timestamp?.seconds * 1000).toLocaleTimeString()}
                                                    </span>
                                                    {latestLocation.location?.speed !== undefined && (
                                                        <span>
                                                            Speed: {latestLocation.location.speed?.toFixed(1) || '0'} km/h
                                                        </span>
                                                    )}
                                                </div>
                                                <a
                                                    href={`https://www.google.com/maps?q=${latestLocation.location?.latitude},${latestLocation.location?.longitude}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 mt-1"
                                                >
                                                    <ExternalLink className="w-3 h-3" />
                                                    Open in Google Maps
                                                </a>
                                            </div>
                                        ) : (
                                            <p className="text-sm text-blue-600">No location data available yet</p>
                                        )}
                                    </div>

                                    {/* Location History */}
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                                            <Clock className="w-4 h-4" />
                                            Location History ({locationHistory.length} updates)
                                        </h4>
                                        <div className="max-h-[300px] overflow-y-auto space-y-2">
                                            {locationHistory.length === 0 ? (
                                                <p className="text-sm text-gray-400">No location history</p>
                                            ) : (
                                                locationHistory.slice().reverse().map((loc, index) => {
                                                    const lat = loc.location?.latitude ?? loc.latitude;
                                                    const lng = loc.location?.longitude ?? loc.longitude;

                                                    return (
                                                        <div key={loc.id || index} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg border border-gray-100">
                                                            <div className={`w-2 h-2 rounded-full ${index === 0 ? 'bg-green-500' : 'bg-blue-400'}`}></div>
                                                            <div className="flex-1">
                                                                <p className="text-xs text-gray-700 font-mono">
                                                                    {loc.locationName || `${lat?.toFixed(4)}, ${lng?.toFixed(4)}`}
                                                                </p>
                                                                <div className="flex items-center gap-3 text-[10px] text-gray-400">
                                                                    <span>
                                                                        {loc.timestamp?.seconds
                                                                            ? new Date(loc.timestamp.seconds * 1000).toLocaleTimeString()
                                                                            : 'N/A'}
                                                                    </span>
                                                                    {loc.location?.accuracy && (
                                                                        <span>±{loc.location.accuracy.toFixed(0)}m</span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            {lat != null && lng != null && (
                                                                <a
                                                                    href={`https://www.google.com/maps?q=${lat},${lng}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-blue-500 hover:text-blue-700 text-xs"
                                                                >
                                                                    <Eye className="w-3.5 h-3.5" />
                                                                </a>
                                                            )}
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </div>

                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default LiveTracking;